import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import KoreanLunarCalendar from 'korean-lunar-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'moment/locale/ko';

moment.locale('ko');
const localizer = momentLocalizer(moment);

const EVENT_TYPES = {
  major:    { label: '주요행사', color: '#c0392b' },
  seasonal: { label: '절기',    color: '#c8a96e' },
  ceremony: { label: '의식',    color: '#7c5cbf' },
  personal: { label: '개인알림', color: '#009590' },
};

const BuddhistCalendar = () => {
  const [events, setEvents] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(moment().month());
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [form, setForm] = useState({
    title: '',
    date: moment().format('YYYY-MM-DD'),
    email: '',
    notification: false,
  });
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    const calendar = new KoreanLunarCalendar();
    
    const getSolar = (lYear, lMonth, lDay) => {
      calendar.setLunarDate(lYear, lMonth, lDay, false);
      const s = calendar.getSolarCalendar();
      return new Date(s.year, s.month - 1, s.day);
    };

    let newEvents = [];
    // 앞뒤 1년씩 생성하여 월 이동 시 끊김 없이 보이도록 함
    const years = [selectedYear - 1, selectedYear, selectedYear + 1];

    years.forEach(year => {
      // 불교 4대 명절 및 주요 기념일
      newEvents.push({ id: `major-4-8-${year}`, title: '부처님 오신 날', start: getSolar(year, 4, 8), end: getSolar(year, 4, 8), allDay: true, type: 'major' });
      newEvents.push({ id: `major-7-15-${year}`, title: '백중 (우란분절)', start: getSolar(year, 7, 15), end: getSolar(year, 7, 15), allDay: true, type: 'major' });
      newEvents.push({ id: `major-12-8-${year}`, title: '성도재일', start: getSolar(year, 12, 8), end: getSolar(year, 12, 8), allDay: true, type: 'major' });
      newEvents.push({ id: `major-2-8-${year}`, title: '출가재일', start: getSolar(year, 2, 8), end: getSolar(year, 2, 8), allDay: true, type: 'major' });
      newEvents.push({ id: `major-2-15-${year}`, title: '열반재일', start: getSolar(year, 2, 15), end: getSolar(year, 2, 15), allDay: true, type: 'major' });

      // 양력 절기 (고정)
      newEvents.push({ id: `season-12-22-${year}`, title: '동지', start: new Date(year, 11, 22), end: new Date(year, 11, 22), allDay: true, type: 'seasonal' });
      newEvents.push({ id: `season-1-1-${year}`, title: '신정', start: new Date(year, 0, 1), end: new Date(year, 0, 1), allDay: true, type: 'seasonal' });

      // 매월 재일
      for (let m = 1; m <= 12; m++) {
        newEvents.push({ id: `ceremony-${year}-${m}-1`, title: `${m}월 초하루 (신중재일)`, start: getSolar(year, m, 1), end: getSolar(year, m, 1), allDay: true, type: 'ceremony' });
        newEvents.push({ id: `ceremony-${year}-${m}-15`, title: `${m}월 보름 (미타재일)`, start: getSolar(year, m, 15), end: getSolar(year, m, 15), allDay: true, type: 'ceremony' });
        newEvents.push({ id: `ceremony-${year}-${m}-18`, title: '지장재일', start: getSolar(year, m, 18), end: getSolar(year, m, 18), allDay: true, type: 'ceremony' });
        newEvents.push({ id: `ceremony-${year}-${m}-24`, title: '관음재일', start: getSolar(year, m, 24), end: getSolar(year, m, 24), allDay: true, type: 'ceremony' });
      }
    });

    setEvents(newEvents);
    const savedReminders = JSON.parse(localStorage.getItem('buddhistReminders') || '[]');
    setReminders(savedReminders);
  }, [selectedYear]);

  const handleMonthChange = (date) => {
    setSelectedMonth(moment(date).month());
    setSelectedYear(moment(date).year());
  };

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const addReminder = (e) => {
    e.preventDefault();
    if (!form.title || !form.date) return;
    const newReminder = { id: Date.now(), ...form };
    const updated = [...reminders, newReminder];
    setReminders(updated);
    localStorage.setItem('buddhistReminders', JSON.stringify(updated));
    setForm({ title: '', date: moment().format('YYYY-MM-DD'), email: '', notification: false });
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  };

  const deleteReminder = (id) => {
    const updated = reminders.filter((r) => r.id !== id);
    setReminders(updated);
    localStorage.setItem('buddhistReminders', JSON.stringify(updated));
  };

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: EVENT_TYPES[event.type]?.color || '#009590',
      borderRadius: '3px',
      color: 'white',
      border: 'none',
      fontSize: '0.74rem',
      padding: '2px 5px',
    },
  });

  const allEvents = [
    ...events,
    ...reminders.map((r) => ({
      id: `reminder-${r.id}`,
      title: `🔔 ${r.title}`,
      start: new Date(r.date),
      end: new Date(r.date),
      allDay: true,
      type: 'personal',
    })),
  ];

  const monthEvents = events.filter(
    (e) => moment(e.start).month() === selectedMonth && moment(e.start).year() === selectedYear
  );

  return (
    <div className="cal-wrap">
      {/* ── 히어로 ── */}
      <section className="cal-hero">
        <div className="cal-hero-dharma" aria-hidden="true">☸</div>
        <Container className="cal-hero-inner">
          <span className="cal-badge">불교 달력</span>
          <h1 className="cal-hero-title">불교 달력 · 알림</h1>
          <p className="cal-hero-sub">
            주요 불교 행사와 절기를 달력으로 확인하고<br />
            개인 기도 알림을 설정해 보세요.
          </p>
        </Container>
      </section>

      <Container className="cal-container">
        {/* 성공 알림 */}
        {showAlert && (
          <div className="cal-alert">
            <span>🙏</span> 알림이 성공적으로 등록되었습니다.
            <button className="cal-alert-close" onClick={() => setShowAlert(false)}>×</button>
          </div>
        )}

        <Row className="g-4">
          {/* ── 달력 ── */}
          <Col md={8}>
            <div className="cal-card">
              <div className="cal-card-head">
                <span className="cal-section-label">☸ 불교 행사 달력</span>
              </div>
              <div className="cal-calendar-wrap">
                <Calendar
                  localizer={localizer}
                  events={allEvents}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: 520 }}
                  eventPropGetter={eventStyleGetter}
                  onNavigate={handleMonthChange}
                  views={['month']}
                  defaultView="month"
                  messages={{ next: '다음', previous: '이전', today: '오늘', month: '월' }}
                />
              </div>
            </div>
          </Col>

          {/* ── 사이드바 ── */}
          <Col md={4}>

            {/* 이번 달 행사 */}
            <div className="cal-card cal-card--sm mb-4">
              <div className="cal-card-head">
                <span className="cal-section-label">
                  ☸ {selectedYear}년 {selectedMonth + 1}월 행사
                </span>
              </div>

              {monthEvents.length > 0 ? (
                <ul className="cal-event-list">
                  {monthEvents.map((ev) => (
                    <li key={ev.id} className="cal-event-item">
                      <span
                        className="cal-event-dot"
                        style={{ background: EVENT_TYPES[ev.type]?.color }}
                      />
                      <div>
                        <span className="cal-event-tag" style={{ color: EVENT_TYPES[ev.type]?.color }}>
                          {EVENT_TYPES[ev.type]?.label}
                        </span>
                        <span className="cal-event-date">
                          {moment(ev.start).format('MM월 DD일')}
                        </span>
                        <strong className="cal-event-title">{ev.title}</strong>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="cal-empty">이번 달 등록된 행사가 없습니다.</p>
              )}

              {/* 범례 */}
              <div className="cal-legend">
                {Object.entries(EVENT_TYPES).map(([key, val]) => (
                  <div key={key} className="cal-legend-item">
                    <span className="cal-legend-dot" style={{ background: val.color }} />
                    <span>{val.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 알림 등록 */}
            <div className="cal-card cal-card--sm">
              <div className="cal-card-head">
                <span className="cal-section-label">🔔 개인 기도 알림</span>
              </div>

              <form onSubmit={addReminder} className="cal-form">
                <div className="cal-form-group">
                  <label className="cal-label">알림 제목</label>
                  <input
                    className="cal-input"
                    type="text"
                    name="title"
                    value={form.title}
                    onChange={handleInput}
                    placeholder="기도 또는 행사 제목"
                    required
                  />
                </div>
                <div className="cal-form-group">
                  <label className="cal-label">날짜</label>
                  <input
                    className="cal-input"
                    type="date"
                    name="date"
                    value={form.date}
                    onChange={handleInput}
                    required
                  />
                </div>
                <div className="cal-form-group">
                  <label className="cal-label">이메일 (선택)</label>
                  <input
                    className="cal-input"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleInput}
                    placeholder="알림을 받을 이메일"
                  />
                </div>
                <label className="cal-check-label">
                  <input
                    type="checkbox"
                    name="notification"
                    checked={form.notification}
                    onChange={handleInput}
                    className="cal-check"
                  />
                  이메일로 알림 받기
                </label>
                <button type="submit" className="cal-submit">알림 등록하기</button>
              </form>

              {/* 등록된 알림 목록 */}
              {reminders.length > 0 && (
                <div className="cal-reminder-list">
                  <p className="cal-reminder-count">등록된 알림 {reminders.length}개</p>
                  {reminders.map((r) => (
                    <div key={r.id} className="cal-reminder-item">
                      <div>
                        <strong className="cal-reminder-title">{r.title}</strong>
                        <small className="cal-reminder-date">
                          {moment(r.date).format('YYYY년 MM월 DD일')}
                          {r.notification && r.email && ` · ${r.email}`}
                        </small>
                      </div>
                      <button
                        className="cal-delete-btn"
                        onClick={() => deleteReminder(r.id)}
                        aria-label="삭제"
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </Col>
        </Row>
      </Container>

      {/* ── 스타일 ── */}
      <style jsx="true">{`
        :root {
          --cal-teal: #009590;
          --cal-teal-dark: #007873;
          --cal-teal-pale: #e8f5f5;
          --cal-text: #2c3e35;
          --cal-muted: #6e8a84;
          --cal-border: #e4edec;
        }

        /* 히어로 */
        .cal-hero {
          position: relative;
          background: linear-gradient(160deg, #f0fafa 0%, #e4f5f4 60%, #d5efee 100%);
          padding: 72px 0 64px;
          text-align: center;
          overflow: hidden;
        }
        .cal-hero-dharma {
          position: absolute;
          font-size: 18rem;
          color: var(--cal-teal);
          opacity: 0.04;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
          line-height: 1;
        }
        .cal-hero-inner { position: relative; z-index: 2; }
        .cal-badge {
          display: inline-block;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          color: var(--cal-teal);
          text-transform: uppercase;
          margin-bottom: 12px;
          padding: 5px 14px;
          border: 1px solid var(--cal-teal);
          border-radius: 2px;
        }
        .cal-hero-title {
          font-size: 2.6rem;
          font-weight: 700;
          color: var(--cal-teal);
          letter-spacing: -0.02em;
          margin: 10px 0 16px;
        }
        .cal-hero-sub {
          font-size: 1rem;
          color: var(--cal-muted);
          line-height: 1.9;
          margin: 0;
        }

        /* 컨테이너 */
        .cal-container {
          padding-top: 36px;
          padding-bottom: 64px;
        }

        /* 성공 알림 */
        .cal-alert {
          display: flex;
          align-items: center;
          gap: 10px;
          background: var(--cal-teal-pale);
          border: 1px solid rgba(0,149,144,0.3);
          border-left: 3px solid var(--cal-teal);
          border-radius: 3px;
          padding: 12px 16px;
          font-size: 0.9rem;
          color: var(--cal-text);
          margin-bottom: 24px;
        }
        .cal-alert-close {
          margin-left: auto;
          background: none;
          border: none;
          font-size: 1.2rem;
          color: var(--cal-muted);
          cursor: pointer;
          line-height: 1;
        }

        /* 카드 */
        .cal-card {
          background: #fff;
          border: 1px solid var(--cal-border);
          border-radius: 4px;
          overflow: hidden;
        }
        .cal-card-head {
          padding: 16px 22px;
          border-bottom: 1px solid var(--cal-border);
          background: #fbfefe;
        }
        .cal-section-label {
          font-size: 0.82rem;
          font-weight: 700;
          color: var(--cal-teal);
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        .cal-card--sm .cal-calendar-wrap { display: none; }

        /* 달력 영역 */
        .cal-calendar-wrap {
          padding: 20px;
        }

        /* react-big-calendar 오버라이드 */
        .rbc-month-view {
          border: 1px solid var(--cal-border);
          border-radius: 3px;
          overflow: hidden;
        }
        .rbc-header {
          background: #f9fdfd;
          padding: 8px 0;
          font-size: 0.78rem;
          font-weight: 600;
          color: var(--cal-muted);
          letter-spacing: 0.04em;
          border-bottom: 1px solid var(--cal-border);
        }
        .rbc-today { background: rgba(0,149,144,0.06); }
        .rbc-date-cell {
          font-size: 0.78rem;
          color: var(--cal-text);
          padding: 4px 6px;
        }
        .rbc-off-range .rbc-date-cell { color: #bbb; }
        .rbc-toolbar {
          margin-bottom: 16px;
          font-size: 0.88rem;
        }
        .rbc-toolbar button {
          border: 1px solid var(--cal-border);
          border-radius: 3px;
          padding: 5px 14px;
          color: var(--cal-text);
          background: #fff;
          font-size: 0.82rem;
          transition: all 0.2s;
          font-family: 'Noto Sans KR', sans-serif;
        }
        .rbc-toolbar button:hover,
        .rbc-toolbar button.rbc-active {
          background: var(--cal-teal);
          color: #fff;
          border-color: var(--cal-teal);
        }
        .rbc-toolbar-label {
          font-weight: 700;
          color: var(--cal-text);
          font-size: 1rem;
        }
        .rbc-event { box-shadow: none !important; }
        .rbc-day-bg:hover { background: rgba(0,149,144,0.03); }

        /* 이번 달 행사 리스트 */
        .cal-event-list {
          list-style: none;
          padding: 16px 20px;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .cal-event-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }
        .cal-event-dot {
          flex-shrink: 0;
          width: 8px; height: 8px;
          border-radius: 50%;
          margin-top: 4px;
        }
        .cal-event-tag {
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          margin-right: 6px;
        }
        .cal-event-date {
          font-size: 0.78rem;
          color: var(--cal-muted);
          margin-right: 6px;
        }
        .cal-event-title {
          font-size: 0.88rem;
          color: var(--cal-text);
        }
        .cal-empty {
          padding: 16px 20px;
          font-size: 0.85rem;
          color: var(--cal-muted);
          margin: 0;
        }

        /* 범례 */
        .cal-legend {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          padding: 14px 20px;
          border-top: 1px solid var(--cal-border);
          background: #fbfefe;
        }
        .cal-legend-item {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 0.75rem;
          color: var(--cal-muted);
        }
        .cal-legend-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        /* 알림 폼 */
        .cal-form {
          padding: 18px 20px;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .cal-form-group { display: flex; flex-direction: column; gap: 5px; }
        .cal-label {
          font-size: 0.78rem;
          font-weight: 600;
          color: var(--cal-text);
          letter-spacing: 0.03em;
        }
        .cal-input {
          border: 1px solid var(--cal-border);
          border-radius: 3px;
          padding: 9px 12px;
          font-size: 0.88rem;
          color: var(--cal-text);
          outline: none;
          font-family: 'Noto Sans KR', sans-serif;
          transition: border-color 0.2s;
        }
        .cal-input:focus { border-color: var(--cal-teal); }
        .cal-check-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.82rem;
          color: var(--cal-muted);
          cursor: pointer;
        }
        .cal-check { accent-color: var(--cal-teal); width: 15px; height: 15px; }
        .cal-submit {
          background: var(--cal-teal);
          color: #fff;
          border: none;
          border-radius: 3px;
          padding: 11px;
          font-size: 0.9rem;
          font-weight: 700;
          cursor: pointer;
          letter-spacing: 0.03em;
          font-family: 'Noto Sans KR', sans-serif;
          transition: background 0.25s, transform 0.2s;
        }
        .cal-submit:hover { background: var(--cal-teal-dark); transform: translateY(-1px); }

        /* 등록된 알림 */
        .cal-reminder-list {
          border-top: 1px solid var(--cal-border);
          padding: 14px 20px;
        }
        .cal-reminder-count {
          font-size: 0.75rem;
          font-weight: 700;
          color: var(--cal-teal);
          letter-spacing: 0.06em;
          text-transform: uppercase;
          margin-bottom: 10px;
        }
        .cal-reminder-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 0;
          border-bottom: 1px solid #f0f5f5;
          gap: 10px;
        }
        .cal-reminder-item:last-child { border-bottom: none; }
        .cal-reminder-title {
          display: block;
          font-size: 0.88rem;
          color: var(--cal-text);
          margin-bottom: 3px;
        }
        .cal-reminder-date {
          font-size: 0.74rem;
          color: var(--cal-muted);
          display: block;
        }
        .cal-delete-btn {
          flex-shrink: 0;
          width: 28px; height: 28px;
          border-radius: 50%;
          border: 1px solid #f5c6cb;
          background: #fff8f8;
          color: #e74c3c;
          font-size: 1rem;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: background 0.2s;
          line-height: 1;
        }
        .cal-delete-btn:hover { background: #fde8e8; }

        /* 반응형 */
        @media (max-width: 768px) {
          .cal-hero { padding: 52px 0 44px; }
          .cal-hero-title { font-size: 1.9rem; }
          .cal-container { padding-top: 24px; padding-bottom: 48px; }
          .cal-calendar-wrap { padding: 12px; }
        }
      `}</style>
    </div>
  );
};

export default BuddhistCalendar;